<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import { Search, Settings, Loader2 } from 'lucide-vue-next';
import { Input } from '@/shared/components/ui/input';
import ThemeSwitcher from '@/core/views/layouts/base/components/ThemeSwitcher.vue';
import NavigationUser from '@/core/views/layouts/base/components/NavigationUser.vue';
import NotificationNavigation from '@/core/views/layouts/base/components/NotificationNavigation.vue';
import axiosInstance from '@/shared/utils/axios';
import { usePageTitle } from '@/shared/composables/usePageTitle';
import { useAuthStore } from '@/core/auth/auth';
import { getDefaultAuthorizedRoute } from '@/core/auth/landing';

const emit = defineEmits<{
  (e: 'open-command'): void;
}>();

const router = useRouter();
const { pageTitle, pageSubtitle } = usePageTitle();
const authStore = useAuthStore();

function goBack() {
  if (window.history.length > 1) {
    router.back();
  } else {
    router.push(getDefaultAuthorizedRoute(authStore.user));
  }
}

/** Search & Dropdown State */
const searchQuery = ref('');
const searchResults = ref<any[]>([]);
const isLoading = ref(false);
const showDropdown = ref(false);
const searchContainer = ref<HTMLElement | null>(null);
let debounceTimeout: any = null;

const searchInputRef = ref<any>(null);

function openCommand() {
  emit('open-command');
}

function focusSearchInput() {
  const el = searchInputRef.value;
  if (!el) return;
  const target = el.$el ? el.$el : el;
  if (target && typeof target.focus === 'function') {
    target.focus();
    if (typeof target.select === 'function') {
      target.select();
    }
    showDropdown.value = true;
  }
}

function onGlobalKeydown(e: KeyboardEvent) {
  if ((e.ctrlKey || e.metaKey) && (e.code === 'Space' || e.key === ' ' || e.keyCode === 32)) {
    e.preventDefault();
    focusSearchInput();
    return;
  }

  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
    e.preventDefault();
    openCommand();
  }
}

const performSearch = async () => {
  const query = searchQuery.value.trim();
  if (!query) {
    searchResults.value = [];
    showDropdown.value = false;
    return;
  }

  isLoading.value = true;
  showDropdown.value = true;
  try {
    const { data } = await axiosInstance.get('/api/customers', {
      params: {
        page: 1,
        limit: 10,
        search: query
      }
    });
    searchResults.value = data.customers || [];
  } catch (error) {
    console.error('Error fetching search results:', error);
    searchResults.value = [];
  } finally {
    isLoading.value = false;
  }
};

watch(searchQuery, (newVal) => {
  if (debounceTimeout) clearTimeout(debounceTimeout);
  
  if (!newVal.trim()) {
    searchResults.value = [];
    showDropdown.value = false;
    return;
  }

  debounceTimeout = setTimeout(() => {
    performSearch();
  }, 300);
});

const selectCustomer = (customer: any) => {
  showDropdown.value = false;
  searchQuery.value = '';
  router.push({ name: 'customerDetails', params: { id: customer._id } });
};

const handleClickOutside = (event: MouseEvent) => {
  if (searchContainer.value && !searchContainer.value.contains(event.target as Node)) {
    showDropdown.value = false;
  }
};

onMounted(() => {
  window.addEventListener('keydown', onGlobalKeydown);
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onGlobalKeydown);
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <header
    class="sticky top-0 z-10 flex h-14 shrink-0 items-center justify-between gap-3 border-b border-border/60 bg-card px-4 lg:gap-4 lg:px-6"
  >
    <!-- Left Cluster: Page Title Badge Box -->
    <div
      v-if="pageTitle"
      class="flex min-w-0 items-center gap-2.5 sm:gap-3 rounded-full border border-border/70 bg-gradient-to-r from-background/90 via-muted/30 to-background/90 px-3 py-1.5  select-none"
    >
      <!-- Interactive Morphing Line Back Button -->
      <button
        type="button"
        class="group flex h-6 w-6 shrink-0 items-center justify-center cursor-pointer"
        :title="$t('common.back', 'Go Back')"
        @click="goBack"
      >
        <!-- Morphing Line: Vertical bar "|" that transforms into "<" chevron in LTR and ">" in RTL on hover -->
        <div class="flex flex-col items-center justify-center -space-y-[1px] h-4 w-3 shrink-0">
          <span
            class="h-[9.5px] w-[2.5px] rounded-full bg-primary transition-all duration-300 ease-out origin-bottom group-hover:rotate-[42deg] rtl:group-hover:-rotate-[42deg] group-hover:-translate-x-0.5 rtl:group-hover:translate-x-0.5"
          />
          <span
            class="h-[9.5px] w-[2.5px] rounded-full bg-primary transition-all duration-300 ease-out origin-top group-hover:-rotate-[42deg] rtl:group-hover:rotate-[42deg] group-hover:-translate-x-0.5 rtl:group-hover:translate-x-0.5"
          />
        </div>
      </button>

      <!-- Page Title (Non-clickable) -->
      <div class="flex flex-col justify-center min-w-0 pr-1 rtl:pr-0 rtl:pl-1 text-start">
        <h1 class="text-xs font-bold tracking-tight text-foreground sm:text-sm md:text-base truncate leading-snug">
          {{ pageTitle }}
        </h1>
      </div>
    </div>

    <!-- Right Cluster: Global Search + Actions -->
    <div class="flex shrink-0 items-center gap-2 sm:gap-3 ms-auto">
      <!-- Search Input Container -->
      <div ref="searchContainer" class="relative w-44 sm:w-60 md:w-72 lg:w-80">
        <Search
          class="pointer-events-none absolute left-3 top-1/2 z-[1] h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          ref="searchInputRef"
          v-model="searchQuery"
          type="search"
          :placeholder="$t('sidebar.header.searchPlaceholder')"
          class="h-9 rounded-lg border-border/50 bg-background py-2 pl-9 pr-8 text-sm shadow-sm transition-colors placeholder:text-muted-foreground/90"
          @focus="showDropdown = true"
          @keydown.enter.prevent="openCommand"
        />

        <!-- Search Results Dropdown -->
        <div
          v-if="showDropdown && (isLoading || searchResults.length > 0 || searchQuery.trim())"
          class="absolute right-0 top-full mt-1 z-50 max-h-[600px] w-[26rem] sm:w-[32rem] overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-md"
        >
          <!-- Loading State -->
          <div v-if="isLoading" class="flex items-center justify-center p-4 text-sm text-muted-foreground">
            <Loader2 class="mr-2 h-4 w-4 animate-spin text-primary" />
            Searching...
          </div>

          <!-- No Results State -->
          <div v-else-if="searchResults.length === 0" class="p-4 text-sm text-center text-muted-foreground">
            No customers found.
          </div>

          <!-- Results List -->
          <ul v-else class="py-1">
            <li
              v-for="(customer, index) in searchResults"
              :key="customer._id"
              class="relative flex cursor-pointer select-none flex-col rounded-sm px-3 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
              @click="selectCustomer(customer)"
            >
              <!-- Row 1: Name and Contract ID -->
              <div class="flex justify-between items-center font-medium">
                <span>
                  {{ customer.name }} {{ customer.lastName }}
                  <span v-if="customer.nameFa || customer.lastNameFa" class="text-xs text-muted-foreground ml-1">
                    ({{ customer.nameFa }} {{ customer.lastNameFa }})
                  </span>
                </span>
                <span class="text-xs font-mono bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
                  ID: {{ customer.contractId || 'N/A' }}
                </span>
              </div>

              <!-- Row 2: Integration IDs & Phone -->
              <div class="flex gap-x-3 gap-y-1 mt-1 text-xs text-muted-foreground">
                <span v-if="customer.phone">{{ customer.phone }}</span>
                <span v-if="customer.phone">{{ customer.address }}</span>
              </div>
              
              <!-- Only show hr if it's not the last element -->
              <hr v-if="index < searchResults.length - 1" class="mt-2 border-t border-border">
            </li>
          </ul>

        </div>
      </div>
      <!-- Action Controls -->
      <div class="flex items-center gap-0.5 sm:gap-1">
        <NotificationNavigation trigger-variant="ghost" />

        <ThemeSwitcher trigger-variant="ghost" />

        <RouterLink
          to="/settings"
          class="inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          :title="$t('sidebar.settings')"
        >
          <Settings class="h-[1.125rem] w-[1.125rem]" />
        </RouterLink>

        <NavigationUser :header-mode="true" />
      </div>
    </div>
  </header>
</template>

